import React, { FC, useState } from 'react';
import styles from './styles.module.css';
import { Table, Asset as SharedAsset } from 'Shared/components';
import { formatMoney } from 'Shared/utils';
import {
  transformEnum,
  capitalizeFirstLetter,
} from 'Policies/utils/helpers/helpers';
import { motion, AnimatePresence } from 'framer-motion';
import { CalculatePaymentPlanResponse } from '@clearcover/core-app-types';

type PaymentScheduleProps = {
  paymentScheduleChanges: CalculatePaymentPlanResponse;
  isRenewalPlanChange: boolean;
  totalAmount: number;
};

const headers = ['DUE DATE', 'NEW TOTAL', 'DESCRIPTION'];

const PaymentBreakdown: FC<{
  lineItems: CalculatePaymentPlanResponse['installments'][0]['lineItems'];
}> = ({ lineItems }) => {
  return (
    <div className={styles.paymentBreakdown}>
      {lineItems.map(
        (
          item: CalculatePaymentPlanResponse['installments'][0]['lineItems'][0]
        ) => {
          return (
            <div className={styles.paymentBreakdownItem}>
              <div>{capitalizeFirstLetter(item.name)}</div>
              <div>{formatMoney(item.amount.value)}</div>
            </div>
          );
        }
      )}
    </div>
  );
};

export const PaymentSchedule: FC<PaymentScheduleProps> = ({
  paymentScheduleChanges,
  isRenewalPlanChange,
  totalAmount,
}) => {
  const [openPayments, setOpenPayments] = useState<number[]>([]);

  const newOpenPayments = (current: number[], index: number) =>
    current.includes(index)
      ? current.filter(i => i !== index)
      : [...current, index];

  const installments = paymentScheduleChanges.installments.filter(
    (installment: CalculatePaymentPlanResponse['installments'][0]) =>
      installment.status === 'DUE'
  );

  // Find the first installment due after today
  const getFirstFutureInstallmentAmount = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstFutureInstallment = installments.find(
      (installment: CalculatePaymentPlanResponse['installments'][0]) =>
        installment.status === 'DUE' &&
        installment.type === 'INSTALLMENT' &&
        installment.dueDate &&
        new Date(installment.dueDate) > today
    );

    return firstFutureInstallment?.amount.value || 0;
  };

  const getDownPaymentAmount = () => {
    const downPayment = installments.find(
      (installment: CalculatePaymentPlanResponse['installments'][0]) =>
        installment.type === 'DOWNPAYMENT'
    );

    return downPayment?.amount.value || 0;
  };

  return (
    <div className={styles.premiumContainer}>
      <div className={styles.paymentScheduleContainer}>
        <div className={styles.summaryHeader}>New Payment Schedule</div>
        <Table
          headers={headers}
          columnWidths={[1, 1, 1]}
          className={styles.table}
          striped={false}
          rows={installments.map(
            (
              installment: CalculatePaymentPlanResponse['installments'][0],
              index: number
            ) => {
              const handleClick = () =>
                setOpenPayments(current => newOpenPayments(current, index));

              return {
                key: index,
                data: [
                  installment.dueDate,
                  formatMoney(installment.amount.value),
                  <div className={styles.description}>
                    <span>
                      {capitalizeFirstLetter(transformEnum(installment.type))}
                    </span>
                    <div
                      className={styles.transactionDetailsToggle}
                      onClick={handleClick}
                      onKeyDown={handleClick}
                    >
                      <motion.div
                        initial={false}
                        exit="exit"
                        animate={{
                          transition: { ease: 'easeIn' },
                          rotate: openPayments.includes(index) ? 180 : 0,
                        }}
                      >
                        <SharedAsset variant="upArrow" />
                      </motion.div>
                    </div>
                  </div>,
                  <AnimatePresence initial={false}>
                    {openPayments.includes(index) && (
                      <motion.div
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                          open: {
                            opacity: 1,
                            height: 'auto',
                            transition: { ease: 'easeIn' },
                          },
                          collapsed: {
                            opacity: 0,
                            height: 0,
                            transition: { ease: 'easeOut' },
                          },
                        }}
                      >
                        <PaymentBreakdown lineItems={installment.lineItems} />
                      </motion.div>
                    )}
                  </AnimatePresence>,
                ],
              };
            }
          )}
        />
      </div>
      <div className={styles.billedAmountsContainer}>
        <div className={styles.summaryHeader}>New Billed Amounts</div>
        <div data-testid="policy-summary-premium-change-section">
          <div className={styles.premiumTotals}>
            <div className={styles.premiumLabels}>
              <div className={styles.premiumTotalsHeader}>Total Cost</div>
              <div className={styles.premiumTotalsHeader}>
                Installment Payment Amount
              </div>
              {isRenewalPlanChange ? (
                <div className={styles.premiumTotalsHeader}>
                  Down Payment Amount
                </div>
              ) : (
                <div className={styles.premiumTotalsHeader}>
                  Amount Due Today
                </div>
              )}
            </div>
            <div
              data-testid="premium-change-values"
              className={styles.premiumValues}
            >
              <div
                data-testid="new-total-cost"
                className={styles.premiumTotalValue}
              >
                {formatMoney(totalAmount)}
              </div>
              <div
                data-testid="new-monthly-payment"
                className={styles.premiumTotalValue}
              >
                {formatMoney(getFirstFutureInstallmentAmount())}
              </div>
              {isRenewalPlanChange ? (
                <div
                  data-testid="new-down-payment"
                  className={styles.premiumTotalValue}
                >
                  {formatMoney(getDownPaymentAmount())}
                </div>
              ) : (
                <div
                  data-testid="amount-prior-to-endorsement"
                  className={styles.premiumTotalValue}
                >
                  {formatMoney(
                    paymentScheduleChanges.amountDueToday?.value ?? 0
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
